import { DecodedValue } from '@/popup/compositions/useABIDecoder'
import { extractSignature } from '@/shared/getFunctionSignature'

type Formatter = 'bool' | 'int' | 'string' | 'bytes'

/**
 * Highlight (wrap with HTML tags) different parts of passed function signature.
 *
 * @param signature function signature that should be highlighted
 * @param signatureParams function params
 * @params docs optional documentation for each param
 * @returns function signature with highlighted parts
 */
export const highlightSignature = (
  signature?: string,
  values?: DecodedValue,
  docs?: DecodedValue
): string => {
  if (!signature) {
    return ''
  }

  const extract = extractSignature(signature)
  let { method, params } = extract

  method = '<div class="method">' + method + '(</div>'
  params = params.map((signatureParameter, index) => {
    if (values && values[index] != undefined) {
      const value = values[index]

      return formatParameter(signatureParameter, value, docs && docs[index])
    }

    return '<div class="param">' + signatureParameter + '</div>'
  })
  const ending = '<div class="method">)</div>'
  return method + params.join('') + ending
}

/**
 * Format param with its value using proper formatter function.
 *
 * @param param function param
 * @param values function value
 * @returns formatted param
 */
const formatParameter = (param: string, value: any, doc?: string) => {
  if (param.includes('bytes') || param.includes('address')) {
    return formatBytes(param, value, doc)
  }

  if (param.includes('bool')) {
    return formatBool(param, value, doc)
  }

  if (param.includes('int') || param.includes('fixed')) {
    return formatInt(param, value, doc)
  }

  if (param.includes('string')) {
    return formatString(param, value, doc)
  }

  return ''
}

/**
 * Generate HTML template for type/param/value.
 *
 * @param type represent different types of values
 * @param param function param
 * @param values function value
 * @returns generated html template
 */
const htmlTemplate = (
  type: Formatter,
  param: string,
  values: string | boolean | number | string[] | boolean[] | number[],
  doc?: string
): string => {
  let str = `<div class="param param-${type}">${param}:${
    doc ? ` ${doc}` : ''
  }</div>`

  if (Array.isArray(values)) {
    for (const value of values) {
      str += `<div class="value value-${type} byte-wrap scrollable">${value}</div>`
    }
  } else {
    str += `<div class="value value-${type} byte-wrap scrollable">${values}</div>`
  }
  return str
}

const formatBytes = (
  param: string,
  values: string | string[],
  doc?: string
) => {
  const formattedValues = Array.isArray(values)
    ? values.map(value => `0x${value.replace('0x', '')}`)
    : `0x${values.replace('0x', '')}`
  return htmlTemplate('bytes', param, formattedValues, doc)
}

const formatBool = (
  param: string,
  values: boolean | boolean[],
  doc?: string
) => {
  const formattedValues = Array.isArray(values)
    ? values.map(value => value.toString().toUpperCase())
    : values.toString().toUpperCase()
  return htmlTemplate('bool', param, formattedValues, doc)
}

const formatInt = (param: string, values: number | number[], doc?: string) => {
  return htmlTemplate('int', param, values, doc)
}

const formatString = (
  param: string,
  values: string | string[],
  doc?: string
) => {
  const formattedValues = Array.isArray(values)
    ? values.map(value => `"${value}"`)
    : `"${values}"`
  return htmlTemplate('string', param, formattedValues, doc)
}
